import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { usePageSEO } from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const pad = (n) => String(n).padStart(2, '0');
const fmt = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const mondayOf = (d) => { const x = new Date(d); const wd = (x.getDay() + 6) % 7; return addDays(x, -wd); };
const DAY_NAMES = ['Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato', 'Domenica'];
const STATUS_COLOR = { planned: 'bg-gray-200 text-gray-700', confirmed: 'bg-green-100 text-green-700', recorded: 'bg-blue-100 text-blue-700' };

// Read-only week view of the rundown, shared by link. The API strips guest
// contact details, so nothing private is exposed here.
const ScalettaShared = () => {
  const { token } = useParams();
  usePageSEO({ title: 'Scaletta — Rete Italia', description: 'Scaletta settimanale (sola lettura).', noindex: true });

  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const [data, setData] = useState({ items: [], shows: [] });
  const [state, setState] = useState('loading'); // loading | ok | error

  const load = useCallback(async () => {
    setState('loading');
    try {
      const from = fmt(weekStart), to = fmt(addDays(weekStart, 6));
      const res = await fetch(`${BACKEND_URL}/api/rundown/shared/${token}?from=${from}&to=${to}`);
      if (!res.ok) { setState('error'); return; }
      setData(await res.json());
      setState('ok');
    } catch { setState('error'); }
  }, [token, weekStart]);

  useEffect(() => { load(); }, [load]);

  const showById = useMemo(() => Object.fromEntries((data.shows || []).map((s) => [s.id, s])), [data.shows]);
  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);

  if (state === 'error') {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center px-4">
        <div className="text-center">
          <Lock className="w-10 h-10 mx-auto text-gray-400 mb-3" />
          <h1 className="text-xl font-bold mb-2">Link non valido</h1>
          <p className="text-gray-600">Questo link di condivisione non è più attivo. Chiedi un nuovo link alla produzione.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-3 flex items-center gap-3">
          <img src="/rete-italia-logo.png" alt="Rete Italia" className="h-9" />
          <div>
            <h1 className="text-lg font-bold">Scaletta</h1>
            <p className="text-xs text-gray-500">Sola lettura</p>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between mb-4 bg-white rounded-xl shadow p-3">
          <Button onClick={() => setWeekStart(mondayOf(addDays(weekStart, -7)))} variant="ghost" size="icon"><ChevronLeft /></Button>
          <div className="text-center">
            <div className="font-semibold">Settimana del {weekStart.toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
            <button onClick={() => setWeekStart(mondayOf(new Date()))} className="text-xs text-[#009246] hover:underline">Oggi</button>
          </div>
          <Button onClick={() => setWeekStart(mondayOf(addDays(weekStart, 7)))} variant="ghost" size="icon"><ChevronRight /></Button>
        </div>

        {state === 'loading' ? (
          <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#009246]" /></div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-3">
            {weekDays.map((day, i) => {
              const dateStr = fmt(day);
              const dayItems = (data.items || []).filter((it) => it.date === dateStr);
              return (
                <div key={dateStr} className="bg-white rounded-xl shadow-sm">
                  <div className="px-3 py-2 border-b">
                    <div className="font-semibold text-sm">{DAY_NAMES[i]}</div>
                    <div className="text-xs text-gray-500">{day.toLocaleDateString('it-IT', { day: 'numeric', month: 'short' })}</div>
                  </div>
                  <div className="p-2 space-y-2">
                    {dayItems.length === 0 && <p className="text-xs text-gray-400 text-center py-4">—</p>}
                    {dayItems.map((it, idx) => {
                      const s = showById[it.showId];
                      return (
                        <div key={idx} className="rounded-lg border p-2" style={{ borderLeft: `4px solid ${s?.color || '#9ca3af'}` }}>
                          <div className="flex items-center justify-between gap-1">
                            <span className="text-xs font-semibold text-gray-700">{it.time}</span>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${STATUS_COLOR[it.status] || STATUS_COLOR.planned}`}>{it.status}</span>
                          </div>
                          <div className="text-sm font-medium">{it.title}</div>
                          {s && <div className="text-[11px] text-gray-500">{s.name}</div>}
                          {it.host && <div className="text-[11px] text-gray-500">🎙 {it.host}</div>}
                          {it.guests?.length > 0 && (
                            <div className="text-[11px] text-gray-600 mt-1">
                              {it.guests.map((g, gi) => (
                                <div key={gi}>👤 {g.name}{g.role ? ` — ${g.role}` : ''}</div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ScalettaShared;
